/* ============================================================
   TAO WEARING — GSAP animation controller (v2, robust)
   New file (update-safe). Depends on GSAP core + ScrollTrigger
   (loaded by snippets/tao-assets.liquid).

   ROBUSTNESS MODEL
   - <head> adds <html>.tao-anim synchronously + starts a 3s failsafe.
     While .tao-anim is set, animatable content is hidden by OPACITY only
     (never by transform alone — opacity hiding can't get "stuck visible-less"
     the way an un-animated transform can).
   - This controller animates content's opacity (and transform) to visible.
     Because it sets inline opacity:1, the element shows even if the slide
     part ever misbehaves. It also sets .tao-anim-done so the failsafe stands
     down.
   - No JS / GSAP fail / reduced motion all resolve to plain visible content.

   Declarative attributes:
     [data-tao-intro]  wrapper: its lines/reveals play on LOAD
     [data-tao-lines]  masked headline (children .tao-line > span)
     [data-tao-reveal] fade/slide-up (on scroll, or on load inside intro)
     data-tao-delay    integer; ×0.1s offset
     [data-tao-parallax]="0.2" inside [data-tao-parallax-scope]
     [data-tao-magnetic]       magnetic hover (fine pointers)
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover:hover) and (pointer:fine)').matches;

  function revealAll() { docEl.classList.remove('tao-anim'); }

  /* ---- carousels (functional; runs regardless of motion prefs / GSAP) ----
     Re-binds itself when the track or arrows are replaced, so carousels whose
     markup arrives after load (e.g. <product-recommendations>) still work. */
  function initCarousel(root) {
    var track = root.querySelector('[data-tao-carousel-track]');
    if (!track) return;
    var prev = root.querySelector('[data-tao-carousel-prev]');
    var next = root.querySelector('[data-tao-carousel-next]');

    function step() {
      var card = track.firstElementChild;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 18;
      return card ? card.getBoundingClientRect().width + gap : 320;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth - 4;
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max;
    }

    // same nodes as last time: only the contents changed, so just refresh the arrows
    if (root._taoTrack === track && root._taoNext === next && root._taoPrev === prev) { update(); return; }
    root._taoTrack = track; root._taoNext = next; root._taoPrev = prev;

    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    if (!root._taoWatched) {
      root._taoWatched = true;
      new MutationObserver(function () {
        if (root._taoQueued) return;
        root._taoQueued = true;
        requestAnimationFrame(function () { root._taoQueued = false; initCarousel(root); });
      }).observe(root, { childList: true, subtree: true });
    }
    update();
  }

  function setupCarousels(scope) {
    scope = scope || document;
    if (scope.matches && scope.matches('[data-tao-carousel]')) initCarousel(scope);
    scope.querySelectorAll('[data-tao-carousel]').forEach(initCarousel);
  }
  if (document.readyState !== 'loading') setupCarousels(document);
  else document.addEventListener('DOMContentLoaded', function () { setupCarousels(document); });
  document.addEventListener('shopify:section:load', function (e) { setupCarousels(e.target); });

  if (reduce) { revealAll(); return; }

  var waited = 0;
  var timer = setInterval(function () {
    if (window.gsap && window.ScrollTrigger) { clearInterval(timer); boot(); }
    else if ((waited += 40) > 4000) { clearInterval(timer); revealAll(); }
  }, 40);

  function boot() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    docEl.classList.add('tao-anim-done');

    // Collect the inner spans of a masked-headline element.
    function lineSpans(headEl) { return headEl.querySelectorAll('.tao-line > span'); }

    /* ---- intro: plays on LOAD ---- */
    function initIntro(scope) {
      scope.querySelectorAll('[data-tao-intro]').forEach(function (root) {
        if (root._taoIntro) return;
        root._taoIntro = true;
        var tl = gsap.timeline({ delay: 0.2, defaults: { ease: 'power3.out' } });

        root.querySelectorAll('[data-tao-lines]').forEach(function (head) {
          head._taoLines = true; // claim so the scroll initLines skips it
          var spans = lineSpans(head);
          if (spans.length) {
            tl.fromTo(spans,
              { yPercent: 110, opacity: 0 },
              { yPercent: 0, opacity: 1, duration: 1.1, stagger: 0.09 }, 0);
          }
        });

        root.querySelectorAll('[data-tao-reveal]').forEach(function (el) {
          el._taoRev = true;
          var d = parseFloat(el.dataset.taoDelay || 0) * 0.1;
          tl.to(el, { opacity: 1, y: 0, duration: 0.9 }, 0.2 + d);
        });
      });
    }

    /* ---- masked headline reveal on SCROLL ---- */
    function initLines(scope) {
      scope.querySelectorAll('[data-tao-lines]').forEach(function (head) {
        if (head._taoLines || head.closest('[data-tao-intro]')) return;
        head._taoLines = true;
        var spans = lineSpans(head);
        if (!spans.length) return;
        gsap.fromTo(spans,
          { yPercent: 110, opacity: 0 },
          { yPercent: 0, opacity: 1, duration: 1.05, ease: 'power3.out', stagger: 0.09,
            scrollTrigger: { trigger: head, start: 'top 85%', once: true } });
      });
    }

    /* ---- kinetic words: rise + fade + de-blur, staggered on SCROLL ---- */
    function initWords(scope) {
      scope.querySelectorAll('[data-tao-words]').forEach(function (head) {
        if (head._taoWords) return;
        head._taoWords = true;
        var words = head.querySelectorAll('[data-tao-word]');
        if (!words.length) return;
        gsap.fromTo(words,
          { opacity: 0, y: 18, filter: 'blur(8px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, ease: 'power3.out', stagger: 0.12,
            scrollTrigger: { trigger: head, start: 'top 80%', once: true } });
      });
    }

    /* ---- clip-path image reveal (wipe open + de-zoom) on SCROLL ---- */
    function initClip(scope) {
      scope.querySelectorAll('[data-tao-clip]').forEach(function (el) {
        if (el._taoClip) return;
        el._taoClip = true;
        gsap.fromTo(el, { clipPath: 'inset(0 0 100% 0)' },
          { clipPath: 'inset(0 0 0% 0)', duration: 1.15, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
        var img = el.querySelector('img');
        if (img) {
          gsap.fromTo(img, { scale: 1.12 },
            { scale: 1, duration: 1.4, ease: 'power3.out',
              scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
        }
      });
    }

    /* ---- fade / slide reveal on SCROLL ---- */
    function initReveals(scope) {
      scope.querySelectorAll('[data-tao-reveal]').forEach(function (el) {
        if (el._taoRev || el.closest('[data-tao-intro]')) return;
        el._taoRev = true;
        gsap.to(el, {
          opacity: 1, y: 0, duration: 0.95,
          delay: parseFloat(el.dataset.taoDelay || 0) * 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%', once: true }
        });
      });
    }

    /* ---- parallax ---- */
    function initParallax(scope) {
      scope.querySelectorAll('[data-tao-parallax]').forEach(function (el) {
        if (el._taoPx) return;
        el._taoPx = true;
        var amount = parseFloat(el.dataset.taoParallax || 0.2);
        var trigger = el.closest('[data-tao-parallax-scope]') || el.parentElement;
        gsap.to(el, {
          yPercent: amount * 100, ease: 'none',
          scrollTrigger: { trigger: trigger, start: 'top top', end: 'bottom top', scrub: true }
        });
      });
    }

    /* ---- magnetic buttons ---- */
    function initMagnetic(scope) {
      if (!finePointer) return;
      scope.querySelectorAll('[data-tao-magnetic]').forEach(function (btn) {
        if (btn._taoMag) return;
        btn._taoMag = true;
        var xTo = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'power3' });
        var yTo = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'power3' });
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          xTo((e.clientX - r.left - r.width / 2) * 0.25);
          yTo((e.clientY - r.top - r.height / 2) * 0.35);
        });
        btn.addEventListener('mouseleave', function () { xTo(0); yTo(0); });
      });
    }

    function initAll(scope) {
      scope = scope || document;
      initIntro(scope);
      initLines(scope);
      initWords(scope);
      initClip(scope);
      initReveals(scope);
      initParallax(scope);
      initMagnetic(scope);
      ScrollTrigger.refresh();
    }

    window.TaoMotion = { init: initAll, gsap: gsap };

    if (document.readyState !== 'loading') initAll(document);
    else document.addEventListener('DOMContentLoaded', function () { initAll(document); });

    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  }
})();
